import {useDataForImageRow} from "@components/Booking/SlotPicking/hooks/useDataForImageRow";
import Slots from "@components/Booking/SlotPicking/Slots";
import ImageRow from "@components/ImageRow";
import {BookingPracticeAppointmentReasonFieldsFragment} from "@page-containers/booking/graphql/__generated__/FetchAppointmentReasons.type";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {clearEmail} from "@page-containers/booking/utils/emailStorage";
import {useBookIfUniqueAndGoToConfirmation} from "@page-containers/booking/utils/useBookIfUniqueAndGoToConfirmation";
import {getMilesAway} from "@page-containers/booking/utils/webBookingAnalytics";
import {ApptSlot} from "@services/monolith/availability";
import {useRouter} from "next/router";
import React, {useCallback, useEffect, useState} from "react";
import {useGeolocation} from "src/utils/browser-storage/location";
import {v4} from "uuid";

import {useDefaultPatientQuery} from "../PatientSelection/useDefaultPatientQuery";
import {useFamilyMembersForAuthedPatientQuery} from "../PatientSelection/useFamilyMembersQuery";
import Header from "./components/Header";
import * as slotEvents from "./events";
import {MonolithLocationFields} from "@services/graphql/queries/location";

type SlotsPicker = {
  location: MonolithLocationFields;
  practiceAppointmentReason: BookingPracticeAppointmentReasonFieldsFragment;
  specialtyId: string;
  calendarTime: number;
  isDropoutFlow?: boolean;
  dropoutVisitorId?: string;
};

const SlotPicker = ({
  location,
  practiceAppointmentReason,
  specialtyId,
  calendarTime,
  isDropoutFlow,
  dropoutVisitorId,
}: SlotsPicker): JSX.Element => {
  const [bookAppointmentLoading, setBookAppointmentLoading] = useState(false);
  const [slotOptionsId, setSlotOptionsId] = useState<string>(v4());
  const userPosition = useGeolocation();

  const router = useRouter();

  const [viewId] = useState(v4());

  const apptReasonId = practiceAppointmentReason?.appointmentReason?.id;

  const patientQuery = useDefaultPatientQuery();
  const {familyMembers, loading} = useFamilyMembersForAuthedPatientQuery();

  const patientId = familyMembers[0]?.id;

  useEffect(() => {
    if (userPosition && location) {
      slotEvents.emitTimeStepViewedEvent({
        viewId,
        apptReasonId,
        locationId: location.id,
        milesAway: userPosition && location ? getMilesAway(userPosition, location) : undefined,
        specialtyId,
      });
    }
  }, [apptReasonId, location, specialtyId, userPosition, viewId]);

  const bookAppointment = useBookIfUniqueAndGoToConfirmation(undefined, dropoutVisitorId);

  const onSlotSelection = useCallback(
    async (slot: ApptSlot, practiceId: string, locationSlug: string) => {
      slotEvents.emitSlotClickedEvent(slot.id, slot.time, slotOptionsId, viewId);

      const onlyPatientIdOnAccount = familyMembers.length === 1 && familyMembers[0].id;

      if (onlyPatientIdOnAccount && apptReasonId) {
        try {
          setBookAppointmentLoading(true);
          await bookAppointment(
            {
              apptReasonId: apptReasonId,
              locationId: slot.locationId,
              patientId: onlyPatientIdOnAccount,
              slotIds: slot.slotIds,
              specialtyId,
              virtual: false,
              practiceId,
            },
            {locationSlug, selectedTime: slot.time},
          );
        } finally {
          setBookAppointmentLoading(false);
        }
      } else {
        if (!isDropoutFlow) clearEmail();
        router.push(
          buildBookingLink({
            ...parseBookingQuery(router.query),
            locationSlug,
            selectedTime: slot.time,
            selectedSlotIds: slot.slotIds,
            doctorId: slot.doctorId,
          }),
          undefined,
          {shallow: true},
        );
      }
    },
    [
      apptReasonId,
      bookAppointment,
      familyMembers,
      router,
      slotOptionsId,
      specialtyId,
      viewId,
      isDropoutFlow,
    ],
  );

  const {name, description, src, emphasis} = useDataForImageRow(location, specialtyId);

  return (
    <div className="flex flex-col gap-8">
      <Header apptReasonName={practiceAppointmentReason.appointmentReason?.name} />
      <ImageRow title={name} description={description} emphasis={emphasis} src={src} alt="" />
      <Slots
        onSlotSelection={onSlotSelection}
        reasonId={apptReasonId}
        initialCalendarTime={calendarTime}
        specialtyId={specialtyId}
        location={location}
        slotOptionsId={slotOptionsId}
        setSlotOptionsId={setSlotOptionsId}
        viewId={viewId}
        loading={loading || patientQuery.loading}
        disabled={bookAppointmentLoading}
        patientId={patientId}
      />
    </div>
  );
};

export default SlotPicker;
